import { useState, useEffect } from "react";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Grid, Button, Box, CircularProgress, Typography } from "@mui/material";
import { Col, Row, Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.css";

import {
    DatatableWrapper,
    Pagination,
    PaginationOptions,
    TableBody,
    TableColumnType,
    TableHeader,
} from "react-bs-datatable";

import {
    TextInput,
    disableEnterKey,
    SearchButton,
    TextBackground,
} from "../components";
import { BACKEND_BASE_URL } from "../constants";

// Define the type for the data fetched from the backend
interface ComplexData {
    id: string;
    hash_id: string;
    peptide: string;
    alpha_chain: string;
    beta_chain: string;
    button?: any; // To satisfy the TableColumnType for the button column
}

// Define the schema for the search form inputs
const DB_search_schema = z.object({
    peptide: z.string().optional(),
    TCRalpha: z.string().optional(),
    TCRbeta: z.string().optional(),
});

type DBsearchFormFields = z.infer<typeof DB_search_schema>;

export const SearchDB = () => {
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<ComplexData[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const methods = useForm<DBsearchFormFields>({
        resolver: zodResolver(DB_search_schema),
        defaultValues: { peptide: "", TCRalpha: "", TCRbeta: "" },
    });

    // Function to fetch data from the API
    const fetchData = async (searchParams?: URLSearchParams) => {
        setIsLoading(true);
        let url = `${BACKEND_BASE_URL}/stegg/complex/`;
        if (searchParams && searchParams.toString()) {
            url += `?${searchParams.toString()}`;
        }
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error("Failed to fetch data from server.");
            }
            const data: ComplexData[] = await response.json();
            setTableData(data);
        } catch (error) {
            console.error(error);
            alert("Could not load data from the database.");
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch initial data when the component mounts
    useEffect(() => {
        fetchData();
    }, []);

    // Handle form submission to filter data
    const onSubmit: SubmitHandler<DBsearchFormFields> = (data) => {
        const params = new URLSearchParams();
        if (data.peptide) params.append("peptide", data.peptide);
        if (data.TCRalpha) params.append("alpha_chain", data.TCRalpha);
        if (data.TCRbeta) params.append("beta_chain", data.TCRbeta);
        fetchData(params);
    };

    // Define table headers, including the Analyze button
    const STORY_HEADERS: TableColumnType<ComplexData>[] = [
        { prop: "hash_id", title: "ID", isFilterable: true },
        { prop: "peptide", title: "Peptide", isFilterable: true },
        { prop: "alpha_chain", title: "TCR Alpha", isFilterable: true },
        { prop: "beta_chain", title: "TCR Beta", isFilterable: true },
        {
            prop: "button",
            cell: (row: ComplexData) => (
                <Button
                    onClick={() => navigate(`/analyze/${row.id}`)}
                    variant="contained"
                    sx={{
                        textTransform: "none",
                        backgroundColor: "#0076B6",
                        '&:hover': { backgroundColor: "#00AEEF" },
                    }}
                >
                    Analyze Ensemble
                </Button>
            ),
        },
    ];

    return (
        <FormProvider {...methods}>
            <section className="page">
                <TextBackground>
                    {/* Search Form Section */}
                    <Box component="form" onSubmit={methods.handleSubmit(onSubmit)} onKeyDown={(e) => disableEnterKey(e)} sx={{ mb: 4 }}>
                        <Typography variant="h5" gutterBottom>Search Database</Typography>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} >
                                <TextInput<DBsearchFormFields> name="peptide" label="Peptide Sequence" />
                            </Grid>
                            <Grid item xs={12} >
                                <TextInput<DBsearchFormFields> name="TCRalpha" label="TCR Alpha Sequence" />
                            </Grid>
                            <Grid item xs={12} >
                                <TextInput<DBsearchFormFields> name="TCRbeta" label="TCR Beta Sequence" />
                            </Grid>
                            <Grid item xs={12} >
                                <SearchButton<DBsearchFormFields> onSubmit={onSubmit} />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Data Table Section */}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ wordBreak: 'break-all' }}>
                            <DatatableWrapper
                                body={tableData}
                                headers={STORY_HEADERS}
                                paginationOptionsProps={{
                                    initialState: {
                                        rowsPerPage: 10,
                                        options: [10, 20, 30, 40, 50],
                                    },
                                }}
                            >
                                <Row className="mb-4 p-2">
                                    <Col xs={12} sm={6} lg={4}>
                                        <PaginationOptions />
                                    </Col>
                                    <Col xs={12} sm={6} lg={8} className="d-flex justify-content-end">
                                        <Pagination />
                                    </Col>
                                </Row>
                                <Table striped bordered hover responsive>
                                    <TableHeader />
                                    <TableBody />
                                </Table>
                            </DatatableWrapper>
                        </Box>
                    )}
                </TextBackground>
            </section>
        </FormProvider>
    );
};